import { useEffect, useState } from 'react'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useMproStakeRewardContract } from 'hooks/contracts/useMproStakeRewardContract'
import { logMessage } from 'utils/logMessage'
import dayjs from 'dayjs'
import {
  useGetAvailableStakesQuery,
  useGetCycleConfigQuery,
  useGetLicensesQuery,
  useJoinStakeMutation
} from 'state/services/indexNode/indexNode'
import { useGetTotalRewardToClaimQuery } from 'state/services/indexNodeRewards/indexNodeRewards'
import { useLifetimeTokens } from 'state/global/hooks'
import useGetSingleAccessToken from 'hooks/useGetSingleAccessToken'
import { ProgressState, indexNodeStore } from 'mobxStores/IndexNodeStore'
import isNil from 'lodash/isNil'
import { LicenseType } from '@mprolab/sdk'

export enum AutostakeProgressStatus {
  INITIAL,
  PROGRESS,
  SUCCESS,
  ERROR
}

export const useDeclarationCounter = (
  isStaking: boolean,
  availableStakeAddress?: string,
  loadingExternalResources?: boolean
) => {
  const [timeUntilEnd, setTimeUntilEnd] = useState<number>(0)
  const [progress, setProgress] = useState<number>(100) // Start with 100% progress
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [disableCounter, setDisableCounter] = useState<boolean>(false)

  const { getDeclarationEndTimestamp, getDeclarationStartTimestamp, getUpdateStakersEndTimestamp } =
    useMproStakeRewardContract(availableStakeAddress)

  useEffect(() => {
    let intervalId: NodeJS.Timeout
    const fetchAndSetTimestamp = async () => {
      try {
        let declaredEndTime = await getDeclarationEndTimestamp()
        if (isStaking) {
          declaredEndTime = await getUpdateStakersEndTimestamp()
        }

        const declarationPeriodStartTimestamp = await getDeclarationStartTimestamp()

        // Convert to number dayjs
        const endDate = dayjs.unix(declaredEndTime.toNumber())
        const startDate = dayjs.unix(declarationPeriodStartTimestamp.toNumber())
        const currentDate = dayjs()

        // Check if current time is before the declaration period start time if yes disable counter
        if (currentDate.isBefore(startDate)) {
          setDisableCounter(true)
          setIsLoading(false)
          return
        }

        // Time left in milliseconds
        const millisecondsLeft = endDate.diff(currentDate, 'millisecond')

        // Declaration period duration in milliseconds
        const declarationPeriodDuration = endDate.diff(startDate, 'millisecond')
        // Progress left
        const progressLeft = (millisecondsLeft / declarationPeriodDuration) * 100

        // Set time until end
        setTimeUntilEnd(millisecondsLeft)
        // Set progress left
        setProgress(progressLeft)

        intervalId = setInterval(() => {
          setTimeUntilEnd((prevTime) => {
            const newTime = prevTime - 60000 // Decrement by one minute each interval
            if (newTime <= 0) {
              clearInterval(intervalId)
              return 0 // Prevent negative values
            }

            // Update progress
            const newProgress = (newTime / declarationPeriodDuration) * 100
            setProgress(newProgress)
            return newTime
          })
        }, 60000)
      } catch (error) {
        logMessage(`Failed to fetch declaration end timestamp: ${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    if (!isNil(availableStakeAddress)) {
      fetchAndSetTimestamp()
    } else {
      if (!loadingExternalResources) {
        // If the available stake address is not available and the external resources are not loading
        setIsLoading(false)
      }
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [isStaking, availableStakeAddress, loadingExternalResources])

  return { timeUntilEnd, progress, counterLoading: isLoading, disableCounter }
}

interface AutoStakeConfig {
  initialStakeAmount: number
  apy: number
  totalStakeDuration: number
  remainingStakeDuration: number
  remainingAutostakeCycles: number
  estimatedReward: number
}

export const useStakeConfig = (stakeAddress: string): { config: AutoStakeConfig; isLoading: boolean } => {
  const { account } = useWeb3React()
  const [isLoading, setIsLoading] = useState(true)
  const { getStakeEndTimestamp, getStakeDuration, getUpdateStakersEndTimestamp, getConfigWithAccount } =
    useMproStakeRewardContract(stakeAddress)
  const { data: cycleConfig } = useGetCycleConfigQuery(undefined)
  const { totalReward, totalRewardLoading } = useGetStakeReward()

  const [autoStakeConfig, setAutoStakeConfig] = useState<AutoStakeConfig>({
    initialStakeAmount: 0,
    apy: 0,
    totalStakeDuration: 0,
    remainingStakeDuration: 0,
    remainingAutostakeCycles: 0,
    estimatedReward: 0
  })

  const remainingAutoStakeCycles = async () => {
    // Contract Data
    const stakeEndTimestamp = await getUpdateStakersEndTimestamp()

    // Convert to dayjs
    const currentUnixTime = dayjs().unix()
    const endDate = dayjs.unix(stakeEndTimestamp.toNumber())

    // Cycle config
    const { cycleStartTimestamp, cycleDuration } = cycleConfig

    // Calculate the time difference between the current time and the start of the cycle
    const workingDiff = currentUnixTime - cycleStartTimestamp
    // Calculate the number of full cycles
    const fullCyclesCount = Math.floor(workingDiff / cycleDuration)

    // Calculate the next auto-stake timestamp
    const nextAutoStake = dayjs.unix(cycleStartTimestamp + fullCyclesCount * cycleDuration)
    let nextAutostakeTimestamp = nextAutoStake
    let cycles = 0

    // Loop through and calculate the next auto-stake timestamp until it exceeds the stake end timestamp
    while (nextAutostakeTimestamp.isBefore(endDate)) {
      cycles++
      nextAutostakeTimestamp = nextAutostakeTimestamp.add(cycleDuration, 'second')
    }

    return cycles
  }

  const totalStakeDuration = async () => {
    // Contract Data
    const stakeDuration = await getStakeDuration()
    // Convert to number in milliseconds dayjs
    const stakeDurationInMiliseonds = stakeDuration.toNumber() * 1000
    return stakeDurationInMiliseonds
  }

  const remainingStakeDuration = async () => {
    // Contract Data
    const stakeEndTimestamp = await getStakeEndTimestamp()
    const endDate = dayjs.unix(stakeEndTimestamp.toNumber())
    const currentDate = dayjs()
    // Time left in miliseconds
    const timeLeft = endDate.diff(currentDate, 'millisecond')

    return timeLeft
  }

  useEffect(() => {
    const fetchAllData = async () => {
      const remainingCycles = await remainingAutoStakeCycles()
      const totalDuration = await totalStakeDuration()
      const remainingDuration = await remainingStakeDuration()
      const config = await getConfigWithAccount(account)
      const estimatedReward = totalReward * (config?.stakeConfig?.apy / 100)

      setAutoStakeConfig({
        initialStakeAmount: totalReward,
        apy: config?.stakeConfig?.apy,
        totalStakeDuration: totalDuration,
        remainingStakeDuration: remainingDuration,
        remainingAutostakeCycles: remainingCycles,
        estimatedReward
      })
      setIsLoading(false)
    }

    fetchAllData()
  }, [])

  const loading = isLoading || totalRewardLoading

  return { config: autoStakeConfig, isLoading: loading }
}

export const useJoinAutostake = (availableStakeAddress: string) => {
  const { account } = useWeb3React()
  const [joinProgress, setJoinProgress] = useState(AutostakeProgressStatus.INITIAL)
  const { obtainAccessToken } = useGetSingleAccessToken()
  const [joinStake] = useJoinStakeMutation()

  const handleJoinAutostake = async () => {
    try {
      setJoinProgress(AutostakeProgressStatus.PROGRESS)
      await obtainAccessToken()
      const response = await joinStake({
        account,
        accessToken: indexNodeStore.accessToken,
        stakeContractAddress: availableStakeAddress
      })

      if ('error' in response) {
        throw response.error
      }

      setJoinProgress(AutostakeProgressStatus.SUCCESS)
      indexNodeStore.setProgress(ProgressState.SUCCESS)
    } catch (error) {
      indexNodeStore.setProgress(ProgressState.ERROR)
      setJoinProgress(AutostakeProgressStatus.ERROR)
      logMessage(`Failed to join autostake: ${error}`)
    }
  }

  return { joinProgress, handleJoinAutostake }
}

export const useGetStakeReward = () => {
  const { account } = useWeb3React()
  const lifetimeTokens = useLifetimeTokens()
  const accessToken = lifetimeTokens?.[account]

  const { data: rewardToClaim, isLoading: totalRewardLoading } = useGetTotalRewardToClaimQuery(
    { account, accessToken },
    { skip: isNil(accessToken), refetchOnMountOrArgChange: true }
  )
  const { data: licenses } = useGetLicensesQuery({ account, accessToken }, { skip: isNil(accessToken) })

  const joinedAutostake = licenses?.some((license) => !isNil(license.activeStake))
  // check if any of licenses has an active stake contract address
  const claimable = licenses?.some((license) => license.claimable)
  // check if any of licenses has an status claimable set to true
  const totalReward = rewardToClaim?.totalToClaim || 0
  // Distribution the payout guards held back — a listed or just-transferred
  // licence. Surfaced so a shrinking balance reads as a reason, not a loss.
  const withheldMpro = rewardToClaim?.withheldMpro || 0
  const withheldLicenses = rewardToClaim?.withheldLicenses || 0
  const hasOnlyMGALicenses = licenses?.every((license) => license.type === LicenseType.MGA_REGULAR)
  const hasMGALicense = licenses?.some((license) => license.type === LicenseType.MGA_REGULAR)
  // `licenses` is undefined until the query returns, and every line around
  // this one already guards for it. Without the guard the first render of a
  // logged-in rewards page throws and takes RewardToClaim down with it.
  const activeStake = licenses?.find((license) => license.activeStake !== undefined)?.activeStake

  return { totalReward, totalRewardLoading, withheldMpro, withheldLicenses, joinedAutostake, claimable, hasOnlyMGALicenses, hasMGALicense, activeStake }
}

export const useGetAvailableStake = (): {
  availableStakeAddress: string
  isLoading: boolean
} => {
  const { account } = useWeb3React()
  const lifetimeTokens = useLifetimeTokens()
  const accessToken = lifetimeTokens?.[account]

  const { data: availableStakes, isLoading } = useGetAvailableStakesQuery(
    { account, accessToken },
    { skip: isNil(accessToken) }
  )

  // For now we would only have one available stake per user
  return { availableStakeAddress: availableStakes?.availableStakes?.[0], isLoading }
}
